/**
 * Shared anti-spam + contact validation for every lead form.
 * The same rules are mirrored server-side in supabase/functions/_shared/leadGuard.ts
 * and in the `validate_lead_contact` database trigger.
 */

export const MIN_FILL_MS = 3000;

export const DISPOSABLE_DOMAINS = [
  "mailinator.com", "yopmail.com", "yopmail.net", "guerrillamail.com", "guerrillamail.net",
  "sharklasers.com", "10minutemail.com", "10minutemail.net", "temp-mail.org", "tempmail.com",
  "tempmail.net", "temp-mail.io", "trashmail.com", "trashmail.de", "getnada.com", "nada.email",
  "dispostable.com", "maildrop.cc", "throwawaymail.com", "fakeinbox.com", "mintemail.com",
  "mohmal.com", "emailondeck.com", "tempr.email", "discard.email", "spamgourmet.com",
  "mailnesia.com", "mytemp.email", "burnermail.io", "moakt.com", "tmail.ws", "grr.la",
];

const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export const isValidEmailFormat = (email: string) => EMAIL_RE.test(email.trim());

export const isDisposableEmail = (email: string) => {
  const domain = email.trim().toLowerCase().split("@")[1] ?? "";
  return DISPOSABLE_DOMAINS.includes(domain);
};

/** Valid, non-disposable email. */
export const isAcceptableEmail = (email: string) =>
  isValidEmailFormat(email) && !isDisposableEmail(email);

/** International phone: optional +, 8-15 digits; spaces, dashes, dots and parentheses allowed. */
export const isValidPhone = (phone: string) => {
  const digits = phone.trim().replace(/[\s\-().]/g, "");
  return /^\+?[0-9]{8,15}$/.test(digits);
};

export type GuardInput = {
  honeypot: string;
  startedAt: number;
  now?: number;
};

/** True when the submission looks automated (honeypot filled or filled too fast). */
export const isSpamSubmission = ({ honeypot, startedAt, now = Date.now() }: GuardInput) =>
  honeypot.trim().length > 0 || now - startedAt < MIN_FILL_MS;

export type Division = "retail" | "industrial" | "medical";

/** Division from the current URL (language prefix allowed). */
export const divisionFromPath = (pathname: string): Division => {
  const p = pathname.replace(/^\/(es|en|it|de|fr)(?=\/|$)/, "");
  if (p.startsWith("/industrial")) return "industrial";
  if (p.startsWith("/medical")) return "medical";
  return "retail";
};

/** Space type overrides the page division when it clearly belongs to another one. */
export const divisionFromSpace = (space: string | undefined, fallback: Division): Division => {
  if (space === "industria") return "industrial";
  if (space === "hospital") return "medical";
  return fallback;
};
